/**
 * The **Cinema Day** — a cinema's business day, running 04:00 until 03:59:59.999 the next morning,
 * so a late screening belongs to the day it was programmed under rather than the calendar day it
 * ends in. See CONTEXT.md → "Programme & scheduling".
 *
 * This is the only definition of that boundary. It used to exist six times: once here (correct) and
 * once in each of admin, distributor, cinema, cinema-website and b2b-marketing, all five of which
 * ended the day at 03:59:00.000 and so dropped the last 59.999 seconds — a nightly window in which a
 * showing belonged to no Cinema Day at all (CIN-14663).
 */

import {
    calendarDayEndInZone as calendarDayEndOfKey,
    calendarDayStartInZone as calendarDayStartOfKey,
    type DayKey,
    isDayKey,
    zonedDayKey
} from './cinema-time-zone.ts';
// `TimeZone` is a bare type alias, so it is imported as a type: `locale-helper` reaches react and
// @cineamo/api at runtime, and the "[frontend] Tests" step runs with no node_modules. The
// type-stripper erases this line; a value import would abort test/cinema-day.test.mjs in CI.
import type { TimeZone } from './locale-helper';
import { cinemaWallClockToInstant, instantToCinemaWallClock } from './time-zone.ts';

/** The hour a Cinema Day begins. Everything below derives its boundary from this. */
export const CINEMA_DAY_START_HOUR = 4;

/**
 * The calendar day `date` names in `timeZone`: a bare `YYYY-MM-DD` *names a day*, so it is taken
 * verbatim rather than parsed — `new Date('2026-10-17')` is UTC midnight, which in Berlin is 02:00
 * on the 17th and once opened that day two hours late (CIN-15254). Anything else is an instant. The
 * two shapes genuinely meet here: the calendar bar hands back a day key while the date stepper hands
 * back an ISO instant, through the same `selectedStartDatetime`.
 */
function toDayKey(date: Date | string, timeZone: TimeZone): DayKey {
    return typeof date === 'string' && isDayKey(date.trim()) ? date.trim() : zonedDayKey(new Date(date), timeZone);
}

/**
 * The instant the calendar day containing `date` begins at the cinema — midnight on its own clock.
 * Accepts an instant or a day key; the arithmetic is `cinema-time-zone.ts`'s, so neither the
 * viewer's zone nor a DST night in it can move the boundary.
 *
 * The listing surfaces file a screening under the calendar day it starts on, so that a 01:00 show
 * appears on the day its clock reads rather than under the previous evening (CIN-15254).
 */
export function calendarDayStartInZone(date: Date | string, timeZone: TimeZone): Date {
    return calendarDayStartOfKey(toDayKey(date, timeZone), timeZone);
}

/** The last instant of the calendar day containing `date` at the cinema — 23:59:59.999 on its clock. */
export function calendarDayEndInZone(date: Date | string, timeZone: TimeZone): Date {
    return calendarDayEndOfKey(toDayKey(date, timeZone), timeZone);
}

/** The calendar date (`YYYY-MM-DD`) `instant` falls on at the cinema — the key days are grouped by. */
export function calendarDayKeyInZone(instant: Date | string, timeZone: TimeZone): DayKey {
    return toDayKey(instant, timeZone);
}

/** 04:00 on the calendar day of `date`. */
export function cinemaDayStart(date: Date | string): Date {
    const start = new Date(date);
    start.setHours(CINEMA_DAY_START_HOUR, 0, 0, 0);
    return start;
}

/** The last instant of the Cinema Day that began on `date` — 03:59:59.999 the following morning. */
export function cinemaDayEnd(date: Date | string): Date {
    const end = new Date(date);
    end.setHours(CINEMA_DAY_START_HOUR - 1, 59, 59, 999);
    end.setDate(end.getDate() + 1);
    return end;
}

/**
 * The Cinema Day `instant` actually falls inside, which before 04:00 is still the previous calendar
 * day's.
 *
 * Anchoring on a raw `new Date()` at 02:00 would produce a window running 04:00 today to 03:59
 * tomorrow — entirely in the future, so anything counting over it reports zero for the four hours
 * after midnight.
 */
export function cinemaDayOf(instant: Date | string): Date {
    const anchor = new Date(instant);
    if (anchor.getHours() < CINEMA_DAY_START_HOUR) {
        anchor.setDate(anchor.getDate() - 1);
    }
    anchor.setHours(CINEMA_DAY_START_HOUR, 0, 0, 0);
    return anchor;
}

/**
 * The calendar month `date` falls in, on the cinema's clock, as an instant range: midnight in
 * `timeZone` on the 1st (inclusive) to midnight there on the 1st of the following month
 * (exclusive). `monthKey` is that month as `YYYY-MM-01`. The range every day cell of a calendar
 * month shares when it asks for showing counts (CIN-11744).
 *
 * It used to run 04:00 to 04:00 — the Cinema Days of that month — which is the wrong window now
 * that the day cells beneath it are calendar days (CIN-15254): the 1st lost its first four hours
 * and the month reached into the next one. The backend counts on the same midnight boundary and
 * reports `dayStartHour: 0`, so the window, the grouping and the rendered list all agree.
 */
export function calendarMonthWindow(
    date: Date | string,
    timeZone: TimeZone
): { monthKey: string; start: Date; end: Date } {
    const wallClock = instantToCinemaWallClock(date, timeZone);
    const year = wallClock.getFullYear();
    const month = wallClock.getMonth();
    const startWallClock = new Date(year, month, 1, 0, 0, 0, 0);
    const endWallClock = new Date(year, month + 1, 1, 0, 0, 0, 0);
    return {
        monthKey: `${year}-${String(month + 1).padStart(2, '0')}-01`,
        start: cinemaWallClockToInstant(startWallClock, timeZone),
        end: cinemaWallClockToInstant(endWallClock, timeZone)
    };
}
